// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import type {VNode} from 'snabbdom';

import {ButtonType} from 'common/components/button/constants';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {IronContextRegistry} from 'common/report/context-registry';
import {LocalizationStore} from 'common/i18n/store';
import {i18next} from 'common/i18n';

import {InputInputEvent, InputKeyboardEvent} from '../input/types';
import {promisifyModal} from '../modal/utils';
import {ConfirmAttrs, ConfirmParams, ConfirmProps, THEME, ValidatorFn} from './types';
import {template} from './template';
import css from './index.styl?inline';

interface ConfirmState {
  inputValue?: string;
  inputError: string | null;
}

interface ConfirmJsxProps {
  attrs?: ConfirmAttrs;
  params?: ConfirmParams;
  on?: {
    change?: (ev: ChangeEvent) => void;
    submit?: (ev: SubmitEvent) => void;
  };
}

export type ChangeEvent = CustomEvent<{state: string}>;

export type SubmitEvent = CustomEvent<{action: `confirm`; inputValue: string} | {action: `cancel`}>;

export class Confirm extends Component<ConfirmState, any, unknown, unknown, IronContextRegistry, ConfirmParams> {
  declare inputValidator: ValidatorFn;
  declare bannerContent: VNode;

  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'hide-cancel-button': `boolean`,
      'cancel-button-label': {type: `string`},
      closeable: `boolean`,
      'not-fullscreen': `boolean`,
      'modal-type': {type: `string`, default: undefined, enum: [`popup`]},
      'confirm-button-disabled': `boolean`,
      'grow-to-content': `boolean`,
      'confirm-button-label': {type: `string`},
      open: `boolean`,
      title: `string`,
      role: `string`,
      description: `string`,
      'description-use-markdown': `boolean`,
      'description-allow-newline': {
        type: `boolean`,
        description: `Allows escape new lines and line breaks to be rendered in new lines.`,
      },
      theme: {
        type: `string`,
        default: THEME.CONFIRM,
        enum: Object.values(THEME),
      },
      'show-input': {
        type: `boolean`,
        description: `Presents a required input field to the user. The result is provided in the confirm event: details.inputValue.`,
      },
      'initial-input-value': {
        type: `string`,
        default: ``,
        description: `Provides an initial value for the input field.`,
      },
      'allow-empty-input-value': {
        type: `boolean`,
        description: `Allows the input field to be submitted as an empty value.`,
      },
      'banner-text': {
        type: `string`,
      },
      'bottom-banner-text': {
        type: `string`,
        description: `Displays an info banner at the bottom of the modal content.`,
      },
      'remove-host-style': {
        type: `boolean`,
        description: `Prevent adding absolute host-style to modal`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        inputValue: ``,
        inputError: null,
      },

      contexts: [`i18n` as const],

      defaultContexts: {
        i18n: new LocalizationStore(i18next),
      },

      params: {
        confirmButtonLabel: String,
        title: String,
        description: String,
      },

      helpers: {
        ButtonType,
        modalChanged: ({detail}: CustomEvent) => {
          this.dispatchEvent(new CustomEvent(`change`, {detail}));
        },

        cancel: () => {
          const ev = new CustomEvent(`submit`, {cancelable: true, detail: {action: `cancel`}});
          this.dispatchEvent(ev);
          if (!ev.defaultPrevented) {
            this.setAttribute(`open`, `false`);
          }
        },

        confirm: () => {
          this.confirm();
        },

        isConfirmButtonDisabled: (): boolean => {
          const isValidInputState = this.attr(`allow-empty-input-value`) || Boolean(this.state.inputValue?.trim());
          return this.attr(`confirm-button-disabled`) || (this.attr(`show-input`) && !isValidInputState);
        },

        // Input helpers

        changeInputValue: (value: string, ev: InputInputEvent) => {
          ev.stopPropagation();
          this.update({inputValue: value});
          this.validate();
        },

        commitInputValue: ({keyCode}: InputKeyboardEvent) => {
          if (keyCode === NAVIGATION_KEY_CODES.ENTER && !this.helpers.isConfirmButtonDisabled()) {
            this.helpers.confirm();
          }
        },
      },
    };
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `initial-input-value`:
        this.update({inputValue: newValue});
        break;
      case `input-error-message`:
        this.update({inputError: newValue || null});
        break;
    }
  }

  confirm() {
    this.validate();

    if (!this.state.inputError) {
      const detail = {
        action: `confirm`,
        inputValue: this.state.inputValue,
      };
      const ev = new CustomEvent(`submit`, {cancelable: true, detail});
      this.dispatchEvent(ev);
      if (!ev.defaultPrevented) {
        this.setAttribute(`open`, `false`);
      }
    }
  }

  private validate() {
    if (!this.inputValidator) {
      return;
    }

    this.update({inputError: this.inputValidator(this.state.inputValue)});
  }
}

// `change` and `submit` are HTML native events, snabbdom module does not like us to re-patch them as custom events
// we should move toward `params.onConfirm` `params.onChange` etc. to avoid overriding native HTML events
export const ConfirmEl = defineAndMakeJsxFactory<Omit<ConfirmJsxProps, `on`>>(`mp-confirm`, Confirm);
export const ConfirmReact = panel2react<ConfirmJsxProps>(`mp-confirm`, Confirm);

export async function confirm(
  attrs: ConfirmAttrs,
  props: ConfirmProps = {},
  params: ConfirmParams = {},
): Promise<boolean> {
  const finalAttrs = {role: `dialog`, ...attrs};
  const closeEv = await promisifyModal({
    panelElement: ConfirmEl,
    attrs: finalAttrs,
    props,
    params,
    closeEvents: [`submit`],
  });
  return closeEv.detail.action === `confirm`;
}

export function confirmDelete(attrs: ConfirmAttrs): Promise<boolean> {
  return confirm({...attrs, theme: THEME.DELETE});
}

export async function prompt(
  attrs: ConfirmAttrs,
  props: ConfirmProps = {},
  params: ConfirmParams = {},
): Promise<{didConfirm: boolean; inputValue: string}> {
  const closeEv = await promisifyModal({
    panelElement: ConfirmEl,
    attrs: {
      closeable: true,
      'confirm-button-label': `Save`,
      ...attrs,
      elref: `confirmPrompt`,
    },
    props,
    params,
    closeEvents: [`submit`],
  });

  return {
    didConfirm: closeEv.type === `submit` && closeEv.detail.action === `confirm`,
    inputValue: closeEv.type === `submit` ? closeEv.detail.inputValue : null,
  };
}
